// src/pages/MatchInput.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import Panel from "../central/Panel";
import { HeadingMedium, HeadingSmall } from "../central/Typography";
import Button from "../central/Button";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  addDoc,
  where,
  setDoc,
  Timestamp,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { paths } from "../lib/paths";
import { col } from "../lib/fw";

import MatchScoreStep, { computeMatchResult } from "../components/matchInput/MatchScoreStep";
import AppearanceStep from "../components/matchInput/AppearanceStep";
import StarterStep from "../components/matchInput/StarterStep";
import GoalStep from "../components/matchInput/GoalStep";
import CleanSheetStep from "../components/matchInput/CleanSheetStep";
import InfractionsStep from "../components/matchInput/InfractionsStep";
import AssignInfractionStep from "../components/matchInput/AssignInfractionStep";
import BestPlayerStep from "../components/matchInput/BestPlayerStep";
import ReviewSubmitStep from "../components/matchInput/ReviewSubmitStep";
import validateMatchData from "../utils/validateMatchData";
import { logError } from "../utils/logError";
import { logEvent } from "../utils/EventLogger";
import { useAuth } from "../context/AuthContext";
import PageLoader from "../components/PageLoader";
import { useToast } from "../context/ToastContext";

const didAppear = (val) => {
  if (val === true) return true;
  if (typeof val === "object" && val !== null) {
    if (val.appeared === true) return true;
  }
  if (val === false) return false;
  return false;
};

const MatchInput = () => {
  const { leagueID, gameweek, matchID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialStepParam = params.get("step");
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [leagueType, setLeagueType] = useState(null);
  const [seasonID, setSeasonID] = useState(null);

  const [players, setPlayers] = useState([]);
  const stepLabels = [
    "Step 1: Match Score",
    "Step 2: Appearances",
    "Step 3: Starters",
    "Step 4: Goals",
    "Step 5: Clean Sheet",
    "Step 6: Infractions",
    "Step 7: Assign Infractions",
    "Step 8: Bonus Players",
    "Step 9: Review & Submit"
  ];
  const initialStepIndex = initialStepParam
    ? stepLabels.findIndex((l) =>
        l.toLowerCase().includes(initialStepParam.toLowerCase())
      )
    : 0;
  const [step, setStep] = useState(initialStepIndex >= 0 ? initialStepIndex : 0);

  // Availability for this match's gameweek → Set of playerIDs marked available.
  // Drives the green dots on the appearance step and the one-time auto-seed.
  const [availablePlayerIDs, setAvailablePlayerIDs] = useState(new Set());
  const [availabilitySeeded, setAvailabilitySeeded] = useState(false);

  // Match state
  const [matchScore, setMatchScore] = useState({ opponent: "", yourScore: "", theirScore: "" });
  const [appearanceData, setAppearanceData] = useState({});
  const [starterData, setStarterData] = useState({});
  const [goalsData, setGoalsData] = useState([]);
  const [cleanSheet, setCleanSheet] = useState(false);
  const [infractionsSelected, setInfractionsSelected] = useState([]);
  const [infractionAssignments, setInfractionAssignments] = useState({});
  const [bestPlayers, setBestPlayers] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadedExistingMatch, setHasLoadedExistingMatch] = useState(false);
  const [showBonusConfirm, setShowBonusConfirm] = useState(false);
  const [pendingNext, setPendingNext] = useState(false);
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);

  useEffect(() => {
    async function fetchLeagueType() {
      try {
        if (!leagueID) return;
        const snap = await getDoc(doc(db, "leagues", leagueID));
        if (snap.exists()) {
          const data = snap.data();
          if (data?.leagueType) {
            setLeagueType(data.leagueType);
          }
        }
      } catch (err) {
        console.error("Failed to fetch leagueType:", err);
        await logError({
          leagueID,
          userID: "system",
          action: "fetchLeagueType",
          payload: {},
          error: err
        });
      }
    }
    fetchLeagueType();
  }, [leagueID]);

  // Fetch seasonID from league doc
  useEffect(() => {
    async function fetchSeasonID() {
      if (!leagueID) return;
      try {
        const snap = await getDoc(paths.league(leagueID));
        if (snap.exists()) {
          const data = snap.data();
          setSeasonID(data.currentSeasonID || "S1");
        }
      } catch (err) {
        console.error("Failed to fetch seasonID:", err);
        await logError({
          leagueID,
          userID: "system",
          action: "fetchSeasonID",
          payload: {},
          error: err
        });
      }
    }
    fetchSeasonID();
  }, [leagueID]);

  // compute assists dynamically
  const assistsData = useMemo(() => {
    const assistsMap = {};
    goalsData.forEach(({ assist }) => {
      if (assist) {
        assistsMap[assist] = (assistsMap[assist] || 0) + 1;
      }
    });
    return assistsMap;
  }, [goalsData]);

  // fetch players in league/season
  useEffect(() => {
    if (!leagueID || !seasonID) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "leagues", leagueID, "seasons", seasonID, "players"));
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (!cancelled) setPlayers(rows);
      } catch (err) {
        console.error("Error fetching players:", err);
        await logError({
          leagueID,
          userID: "system",
          action: "fetchPlayers",
          payload: {},
          error: err
        });
        if (!cancelled) setPlayers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueID, seasonID]);

  // Fetch availability for THIS match's gameweek and resolve to a Set of playerIDs.
  // Keys in the responses map are either a playerID (unlinked player) or a userUID
  // (member) — mirror PickTeamV2: direct playerID matches are used as-is, userUIDs
  // are resolved via users/{uid}.linkedPlayerIDs[leagueID]. Only players still in
  // the current squad are kept.
  useEffect(() => {
    if (!leagueID || !seasonID || !gameweek || players.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const availRef = doc(
          db,
          "leagues", leagueID,
          "seasons", seasonID,
          "gameweeks", gameweek,
          "availability", "responses"
        );
        const snap = await getDoc(availRef);
        if (!snap.exists()) {
          if (!cancelled) setAvailablePlayerIDs(new Set());
          return;
        }
        const responses = snap.data()?.responses || {};
        const squad = new Set(players.map((p) => p.id));
        const result = new Set();
        await Promise.all(
          Object.entries(responses)
            .filter(([, status]) => status === "available")
            .map(async ([key]) => {
              if (squad.has(key)) {
                result.add(key);
                return;
              }
              try {
                const userSnap = await getDoc(doc(db, "users", key));
                if (userSnap.exists()) {
                  const linked = userSnap.data()?.linkedPlayerIDs?.[leagueID];
                  if (linked && squad.has(linked)) result.add(linked);
                }
              } catch (_) {}
            })
        );
        if (!cancelled) setAvailablePlayerIDs(result);
      } catch (err) {
        console.error("MatchInput: failed to fetch availability", err);
        if (!cancelled) setAvailablePlayerIDs(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueID, seasonID, gameweek, players]);

  // One-time auto-seed: when the appearance step would otherwise start blank,
  // pre-select every available player as "appeared". Skips entirely if any
  // appearances are already present (existing/edited/baseline-copied match), so
  // it never clobbers real data, and runs at most once so a deliberate clear sticks.
  useEffect(() => {
    if (loading || availabilitySeeded) return;
    if (availablePlayerIDs.size === 0) return;
    const alreadyAppeared = Object.values(appearanceData).filter((d) => d?.appeared).length;
    if (alreadyAppeared > 0) {
      setAvailabilitySeeded(true);
      return;
    }
    setAppearanceData((prev) => {
      const next = { ...prev };
      availablePlayerIDs.forEach((pid) => {
        next[pid] = { ...next[pid], appeared: true, isGK: next[pid]?.isGK || false };
      });
      return next;
    });
    setAvailabilitySeeded(true);
  }, [loading, availabilitySeeded, availablePlayerIDs, appearanceData]);

  // Helper to preload appearances/starters from an existing match if creating a new one
  async function loadBaselineFromExistingMatch() {
    try {
      const matchesRef = collection(db, "leagues", leagueID, "seasons", seasonID, "gameweeks", gameweek, "matches");
      const snap = await getDocs(matchesRef);
      if (snap.empty) return;
      // pick the first match doc (any existing match is fine)
      const existing = snap.docs[0].data();
      setAppearanceData(existing.appearanceData || {});
      setStarterData(existing.starters || {});
      // reset everything else
      setGoalsData([]);
      setCleanSheet(false);
      setInfractionsSelected([]);
      setInfractionAssignments({});
      setBestPlayers({});
      // match score is match-specific (not part of the reusable baseline squad) — reset it
      setMatchScore({ opponent: "", yourScore: "", theirScore: "" });
    } catch (err) {
      console.error("Error preloading baseline match:", err);
    }
  }

  // Load match by ID if provided
  useEffect(() => {
    async function loadMatchByID() {
      if (!leagueID || !seasonID || !gameweek || !matchID) return;
      if (matchID === "new") {
        await loadBaselineFromExistingMatch();
        return;
      }
      try {
        const matchRef = doc(db, "leagues", leagueID, "seasons", seasonID, "gameweeks", gameweek, "matches", matchID);
        const snap = await getDoc(matchRef);
        if (snap.exists()) {
          const existing = snap.data();
          setAppearanceData(existing.appearanceData || {});
          setStarterData(existing.starters || {});
          setGoalsData(existing.goals || []);
          setCleanSheet(existing.cleanSheet || false);
          setInfractionsSelected(Object.keys(existing.infractionAssignments || {}));
          setInfractionAssignments(existing.infractionAssignments || {});
          const normalizedBestPlayers = {};
          if (existing.bestPlayers) {
            Object.entries(existing.bestPlayers).forEach(([rank, playerId]) => {
              normalizedBestPlayers[rank] = playerId;
            });
          }
          setBestPlayers(normalizedBestPlayers);
          setMatchScore({
            opponent: existing.opponent || "",
            yourScore: existing.yourScore ?? "",
            theirScore: existing.theirScore ?? "",
          });
          setHasLoadedExistingMatch(true);
        }
      } catch (err) {
        console.error("Error loading match by ID:", err);
      }
    }
    loadMatchByID();
  }, [leagueID, seasonID, gameweek, matchID]);

  // Load previously submitted match data if it exists, unless matchID is provided
  useEffect(() => {
    if (matchID) return;
    if (!leagueID || !seasonID || !gameweek) return;
    (async () => {
      try {
        const matchesRef = collection(db, "leagues", leagueID, "seasons", seasonID, "gameweeks", gameweek, "matches");
        const snapshot = await getDocs(matchesRef);
        if (!snapshot.empty) {
          const existing = snapshot.docs[0].data();
          setAppearanceData(existing.appearanceData || {});
          setStarterData(existing.starters || {});
          setGoalsData(existing.goals || []);
          setCleanSheet(existing.cleanSheet || false);
          setInfractionsSelected(Object.keys(existing.infractionAssignments || {}));
          setInfractionAssignments(existing.infractionAssignments || {});
          // Convert stored player IDs to simple mapping
          const normalizedBestPlayers = {};
          if (existing.bestPlayers) {
            Object.entries(existing.bestPlayers).forEach(([rank, playerId]) => {
              normalizedBestPlayers[rank] = playerId;
            });
          }
          setBestPlayers(normalizedBestPlayers);
          setMatchScore({
            opponent: existing.opponent || "",
            yourScore: existing.yourScore ?? "",
            theirScore: existing.theirScore ?? "",
          });
          setHasLoadedExistingMatch(true);
        }
      } catch (err) {
        console.error("Error loading existing match data:", err);
        await logError({
          leagueID,
          userID: "system",
          action: "loadExistingMatch",
          payload: { gameweek },
          error: err
        });
      }
    })();
  }, [leagueID, seasonID, gameweek, matchID]);

  // Ensure component respects ?step=, and otherwise jumps to Review & Submit if match data is present
  useEffect(() => {
    if (!loading && hasLoadedExistingMatch) {
      const reviewIndex = stepLabels.findIndex(l => l.includes("Review & Submit"));
      const paramStep = initialStepParam
        ? stepLabels.findIndex(l =>
            l.toLowerCase().includes(initialStepParam.toLowerCase())
          )
        : -1;
      if (paramStep >= 0) {
        setStep(paramStep);
      } else {
        setStep(reviewIndex);
      }
    }
  }, [loading, hasLoadedExistingMatch, initialStepParam]);

  // handle submit
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    const matchDoc = {
      leagueID,
      // ✅ use the gameweek from the URL param
      gameweek: gameweek || "Unknown",
      createdAt: Timestamp.fromDate(new Date()),
      appearanceData: appearanceData || {},
      starters: starterData || {},
      goals: goalsData || [],
      assists: assistsData || {},
      cleanSheet: cleanSheet || false,
      infractionAssignments: infractionAssignments || {},
      bestPlayers: bestPlayers || {},
    };

    // Match score fields are entirely optional. Only include fields that were
    // actually filled in — omit the rest so old and un-filled-in matches stay
    // indistinguishable from "not yet entered" on read (no empty strings/zeros).
    const opponentTrimmed = typeof matchScore?.opponent === "string" ? matchScore.opponent.trim() : "";
    if (opponentTrimmed !== "") {
      matchDoc.opponent = opponentTrimmed;
    }
    const yourScoreNum =
      matchScore?.yourScore !== "" && matchScore?.yourScore !== undefined && matchScore?.yourScore !== null
        ? Number(matchScore.yourScore)
        : null;
    const theirScoreNum =
      matchScore?.theirScore !== "" && matchScore?.theirScore !== undefined && matchScore?.theirScore !== null
        ? Number(matchScore.theirScore)
        : null;
    if (yourScoreNum !== null && !Number.isNaN(yourScoreNum)) {
      matchDoc.yourScore = yourScoreNum;
    }
    if (theirScoreNum !== null && !Number.isNaN(theirScoreNum)) {
      matchDoc.theirScore = theirScoreNum;
    }
    const matchScoreResult = computeMatchResult(matchScore?.yourScore, matchScore?.theirScore);
    if (matchScoreResult) {
      matchDoc.result = matchScoreResult;
    }

    // Central validation
    const errors = validateMatchData(matchDoc);
    if (errors.length > 0) {
      showToast("Cannot submit: " + errors.join(", "), "error");
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Submitting match doc:", matchDoc);
      // Conditionally use setDoc or addDoc based on matchID, treating "new" as new
      let matchRef;
      let createdMatchID = "";
      if (!matchID || matchID === "new") {
        // Create a brand new match document with an auto-generated ID
        matchRef = await addDoc(
          collection(db, "leagues", leagueID, "seasons", seasonID, "gameweeks", gameweek, "matches"),
          matchDoc
        );
        createdMatchID = matchRef.id;
      } else {
        // Update the existing match document by ID
        matchRef = doc(db, "leagues", leagueID, "seasons", seasonID, "gameweeks", gameweek, "matches", matchID);
        await setDoc(matchRef, matchDoc);
        createdMatchID = matchRef.id;
      }
      // ✅ Update gameweek phase to "results" after match submission
      const gwRef = doc(db, "leagues", leagueID, "seasons", seasonID, "gameweeks", gameweek);
      await updateDoc(gwRef, { phase: "results" });
      console.log(`Gameweek ${gameweek} phase updated to 'results'`);

      // Usage tracking: match submitted (non-blocking)
      try {
        let resolvedUserName = currentUser?.displayName || "";
        let resolvedRole = "admin";

        if (currentUser?.uid) {
          try {
            const userSnap = await getDoc(doc(db, "users", currentUser.uid));
            if (userSnap.exists()) {
              const data = userSnap.data();
              resolvedUserName =
                data?.displayName ||
                resolvedUserName ||
                currentUser?.email?.split("@")[0] ||
                "";
              const roles = data?.roles || {};
              if (roles && typeof roles === "object" && roles[leagueID]) {
                resolvedRole = roles[leagueID];
              }
            } else {
              resolvedUserName =
                resolvedUserName || currentUser?.email?.split("@")[0] || "";
            }
          } catch {
            resolvedUserName =
              resolvedUserName || currentUser?.email?.split("@")[0] || "";
          }
        }

        await logEvent({
          eventType: "match_submitted",
          leagueID,
          userID: currentUser?.uid || "",
          userName: resolvedUserName,
          role: resolvedRole,
          source: "match_input",
          metadata: {
            seasonID: seasonID || "S1",
            gameweek: gameweek || "",
            matchID: createdMatchID || (matchID || ""),
            isNew: !matchID || matchID === "new",
            appearedCount: Object.values(appearanceData || {}).filter(didAppear)
              .length,
            startersCount: Object.values(starterData || {}).filter(Boolean)
              .length,
            goalsCount: Array.isArray(goalsData) ? goalsData.length : 0,
            cleanSheet: !!cleanSheet,
            infractionsCount: infractionsSelected?.length || 0,
            bestPlayersFilled: [1, 2, 3].filter((r) => bestPlayers?.[r])
              .length,
          },
        });
      } catch (e) {
        console.warn("[analytics] match_submitted failed", e);
      }

      setShowSubmitSuccess(true);
    } catch (err) {
      // Usage tracking: submit error (non-blocking)
      try {
        await logEvent({
          eventType: "page_error_caught",
          leagueID,
          userID: currentUser?.uid || "",
          userName:
            currentUser?.displayName || currentUser?.email?.split("@")[0] || "",
          role: "admin",
          source: "match_input",
          metadata: {
            action: "submitMatch",
            seasonID: seasonID || "S1",
            gameweek: gameweek || "",
            matchID: matchID || "",
            message: err?.message || String(err),
          },
        });
      } catch (e) {
        console.warn("[analytics] page_error_caught (match_input) failed", e);
      }
      console.error("Error submitting match:", err);
      await logError({
        leagueID,
        userID: "system",
        action: "submitMatch",
        payload: { gameweek },
        error: err
      });
      showToast("Failed to submit match data. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    leagueID,
    gameweek,
    matchScore,
    appearanceData,
    starterData,
    goalsData,
    assistsData,
    cleanSheet,
    infractionAssignments,
    bestPlayers,
    navigate,
    matchID,
    seasonID,
    isSubmitting,
    currentUser,
  ]);

  const handleBack = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const validateStep = () => {
    switch (step) {
      // Step 0 (Match Score) is entirely optional — no validation required.
      case 1: {
        const appearedCount = Object.values(appearanceData).filter(didAppear).length;
        const typeNum = typeof leagueType === "string" ? parseInt(leagueType) : leagueType;
        const required = typeNum === 5 ? 5 : typeNum === 7 ? 7 : typeNum === 9 ? 9 : 11;
        return appearedCount >= required || `Please record at least ${required} appearances for a ${typeNum}-a-side league.`;
      }
      case 2: {
        const starterCount = Object.values(starterData).filter(Boolean).length;
        const typeNum = typeof leagueType === "string" ? parseInt(leagueType) : leagueType;
        const required = typeNum === 5 ? 5 : typeNum === 7 ? 7 : typeNum === 9 ? 9 : 11;
        return starterCount === required || `Please select exactly ${required} starters for a ${typeNum}-a-side league.`;
      }
      default:
        return true;
    }
  };

  // conditionally skip Assign Infractions if none selected
  // Make steps static and always include Assign Infractions step
  const steps = useMemo(() => {
    const arr = [];

    // Step 1: Match Score (optional — always first)
    arr.push({
      label: "Step 1: Match Score",
      component: (
        <MatchScoreStep
          matchScore={matchScore}
          setMatchScore={setMatchScore}
        />
      ),
    });

    // Step 2: Appearances
    arr.push({
      label: "Step 2: Appearances",
      component: (
        <AppearanceStep
          players={players}
          appearanceData={appearanceData}
          setAppearanceData={setAppearanceData}
          availablePlayerIDs={availablePlayerIDs}
        />
      ),
    });

    // Step 3: Starters
    arr.push({
      label: "Step 3: Starters",
      component: (
        <StarterStep
          players={players}
          appearanceData={appearanceData}
          starterData={starterData}
          setStarterData={setStarterData}
          leagueType={leagueType}
        />
      ),
    });

    // Step 4: Goals
    const playersAppearedForGoalStep = players.filter((p) =>
      didAppear(appearanceData[p.id])
    );
    arr.push({
      label: "Step 4: Goals",
      component: (
        <GoalStep
          players={playersAppearedForGoalStep}
          goals={goalsData}
          setGoals={setGoalsData}
          appearanceData={appearanceData}
        />
      ),
    });

    // Step 5: Clean Sheet
    arr.push({
      label: "Step 5: Clean Sheet",
      component: (
        <CleanSheetStep
          cleanSheet={cleanSheet}
          setCleanSheet={setCleanSheet}
        />
      ),
    });

    // Step 6: Infractions
    arr.push({
      label: "Step 6: Infractions",
      component: (
        <InfractionsStep
          infractionsSelected={infractionsSelected}
          setInfractionsSelected={setInfractionsSelected}
          cleanSheet={cleanSheet}
          leagueType={leagueType}
        />
      ),
    });

    // Step 7: Assign Infractions (conditionally included)
    if (infractionsSelected && infractionsSelected.length > 0) {
      const playersAppearedForAssignInfractions = players.filter((p) =>
        didAppear(appearanceData[p.id])
      );
      arr.push({
        label: "Step 7: Assign Infractions",
        component: (
          <AssignInfractionStep
            players={playersAppearedForAssignInfractions}
            infractionsSelected={infractionsSelected}
            infractionAssignments={infractionAssignments}
            setInfractionAssignments={setInfractionAssignments}
            cleanSheet={cleanSheet}
            appearanceData={appearanceData}
          />
        ),
      });
    }

    // Step 8 or 7: Bonus Players
    arr.push({
      label: `Step ${arr.length + 1}: Bonus Players`,
      component: (
        <BestPlayerStep
          players={players}
          appearanceData={appearanceData} // ✅ ensures options filter to appeared players
          bestPlayers={bestPlayers}
          setBestPlayers={setBestPlayers}
        />
      ),
    });

    // Step 9 or 8: Review & Submit
    arr.push({
      label: `Step ${arr.length + 1}: Review & Submit`,
      component: (
        <ReviewSubmitStep
          matchScore={matchScore}
          appearanceData={appearanceData}
          starters={starterData}
          goals={goalsData}
          assists={assistsData}
          cleanSheet={cleanSheet}
          infractions={infractionsSelected}
          infractionAssignments={infractionAssignments}
          bestPlayers={bestPlayers}
          players={players}
          onSubmit={handleSubmit}
          onBack={handleBack}
          isSubmitting={isSubmitting}
        />
      ),
    });

    return arr;
  }, [
    matchScore,
    players,
    appearanceData,
    availablePlayerIDs,
    starterData,
    goalsData,
    assistsData,
    cleanSheet,
    infractionsSelected,
    infractionAssignments,
    bestPlayers,
    handleBack,
    handleSubmit,
    leagueType,
  ]);

  const handleNext = () => {
    const bonusStepIndex = steps.findIndex((s) => s.label.includes("Bonus Players"));
    if (step === bonusStepIndex && Object.keys(bestPlayers || {}).length === 0) {
      setShowBonusConfirm(true);
      setPendingNext(true);
      return;
    }
    const valid = validateStep();
    if (valid === true) {
      setStep((s) => s + 1);
    } else {
      showToast(valid, "error");
    }
  };

  const confirmBonusContinue = () => {
    setShowBonusConfirm(false);
    if (pendingNext) {
      setPendingNext(false);
      setStep((s) => s + 1);
    }
  };

  const cancelBonusContinue = () => {
    setShowBonusConfirm(false);
    setPendingNext(false);
  };

  const handleSubmitSuccessClose = () => {
    setShowSubmitSuccess(false);
    navigate(`/admin/${leagueID}`);
  };

  useEffect(() => {
    if (step >= steps.length) {
      setStep(steps.length - 1);
    }
  }, [steps, step]);

  if (loading || !leagueType) {
    return (
      <>
        <PageLoader message="Loading match setup…" />
      </>
    );
  }

  // Defensive check for invalid step index
  if (!steps[step]) {
    console.warn("Invalid step index", step, steps);
    return (
      <>
        <p>Invalid step configuration. Please refresh the page.</p>
      </>
    );
  }

  return (
    <>
      <HeadingMedium>{steps[step].label}</HeadingMedium>
      <HeadingSmall>{gameweek} Match Results </HeadingSmall>

      {/* Progress bar */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            height: 6,
            width: "100%",
            background: "#eee",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${((step + 1) / steps.length) * 100}%`,
              background: "#ff2b6a",
              transition: "width 0.25s ease",
            }}
          />
        </div>
        <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
          Step {step + 1} of {steps.length}
        </div>
      </div>

      <Panel>
        {steps[step].component}
      </Panel>
      {step < steps.length - 1 && (
        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          {step > 0 && (
            <Button onClick={handleBack} variant="blue" disabled={isSubmitting}>
              Back
            </Button>
          )}
          <Button onClick={handleNext} variant="pink" disabled={isSubmitting}>
            Next
          </Button>
        </div>
      )}
      {showBonusConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              maxWidth: "420px",
              textAlign: "center",
            }}
          >
            <h3 style={{ marginTop: 0 }}>No bonus points selected</h3>
            <p style={{ marginBottom: "1rem" }}>
              You haven't selected any bonus players. Continue anyway?
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <Button onClick={cancelBonusContinue}>
                Go Back
              </Button>
              <Button onClick={confirmBonusContinue} variant="pink">
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}
      {showSubmitSuccess && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              maxWidth: "420px",
              width: "90%",
              textAlign: "center",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Results submitted</h3>
            <p style={{ marginBottom: "1rem" }}>
              Match data submitted successfully.
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button onClick={handleSubmitSuccessClose} variant="pink">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MatchInput;